import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  GraduationCap,
  ClipboardList,
  Users,
  BarChart3,
  BadgeCheck,
  ArrowRight,
  Library,
  Video,
  Award
} from "lucide-react";

export default function TrainingAndLMS() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section */}
      <div className="bg-white py-16 px-6 border-b border-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold uppercase tracking-wider mb-6" style={poppinsStyle}>
            <Library size={14} />
            Enterprise Learning Suite
          </div>
          
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Upskill Your Workforce with a <br />
            Modern Learning Management System
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Build a culture of continuous growth. Centralize your training materials, 
            track employee certifications, and automate onboarding learning paths.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-24">
        {/* Core Capabilities Grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-16 mb-20">
          <LMSFeature 
            icon={<BookOpen size={20} />} 
            title="Course Catalog"
            desc="Host self-paced video lessons, PDFs, and interactive SCORM content."
            poppins={poppinsStyle}
          />
          <LMSFeature 
            icon={<Video size={20} />} 
            title="Hybrid Learning"
            desc="Schedule instructor-led virtual sessions or in-person classroom training."
            poppins={poppinsStyle}
          />
          <LMSFeature 
            icon={<ClipboardList size={20} />} 
            title="Assessments"
            desc="Create quizzes and assignments to measure knowledge retention."
            poppins={poppinsStyle}
          />
          <LMSFeature 
            icon={<Users size={20} />} 
            title="Onboarding Paths"
            desc="Automatically assign mandatory training to new hires based on their role."
            poppins={poppinsStyle}
          />
          <LMSFeature 
            icon={<Award size={20} />} 
            title="Certifications"
            desc="Generate branded PDF certificates automatically upon course completion."
            poppins={poppinsStyle}
          />
          <LMSFeature 
            icon={<BarChart3 size={20} />} 
            title="L&D Analytics"
            desc="Track completion rates, quiz scores, and training ROI across teams."
            poppins={poppinsStyle}
          />
        </div>

        {/* Corporate Content Blocks */}
        <div className="grid md:grid-cols-2 gap-12 py-12 border-t border-slate-100">
           <div className="space-y-6">
              <h2 className="text-xl font-medium text-gray-900" style={poppinsStyle}>Why Enterprise LMS?</h2>
              <div className="space-y-4">
                 <BenefitItem label="Standardize compliance training across global locations." />
                 <BenefitItem label="Reduce manual effort in tracking certification expiries." />
                 <BenefitItem label="Improve retention by offering clear career upskilling." />
                 <BenefitItem label="Centralize tribal knowledge into structured digital assets." />
              </div>
           </div>

           <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
              <h3 className="text-lg font-medium text-gray-900 mb-4" style={poppinsStyle}>Operational Control</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                Managers can monitor real-time learning progress for their direct reports, 
                identifying top learners and those who require additional support. 
                Our platform ensures your team stays ahead of industry trends and compliance requirements.
              </p>
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200">
                 <BadgeCheck className="text-blue-500" size={20} />
                 <span className="text-xs font-semibold text-slate-700" style={poppinsStyle}>ISO & Compliance Ready Platform</span>
              </div>
           </div>
        </div>

        {/* Get Started Section */}
        <div className="text-center pt-20 border-t border-slate-100">
          <h3 className="text-xl font-medium text-gray-900 mb-2" style={poppinsStyle}>Enable a culture of continuous learning</h3>
          <p className="text-sm text-gray-500 mb-10">Start building your company's knowledge base today.</p>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-4 bg-gray-900 hover:bg-blue-600 text-white px-6 py-2.5 rounded-full transition-all duration-300 shadow-md"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Access LMS</p>
                <p className="text-sm font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Internal Components for Corporate Look */
function LMSFeature({ icon, title, desc, poppins }) {
  return (
    <div className="p-6 rounded-2xl border border-slate-100 hover:border-blue-100 hover:bg-blue-50/30 transition-colors group">
      <div className="w-10 h-10 rounded-lg bg-slate-50 text-slate-600 group-hover:bg-blue-100 group-hover:text-blue-600 flex items-center justify-center mb-4 transition-colors">
        {icon}
      </div>
      <h4 className="font-medium text-gray-900 text-base mb-2" style={poppins}>{title}</h4>
      <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
  );
}

function BenefitItem({ label }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
}